import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { describeError } from "../../api/client";
import type { Contact } from "../../api/types";
import { EmptyState } from "../../components/EmptyState";
import { Field } from "../../components/Field";
import { FeedbackPanel } from "../../components/FeedbackPanel";
import { Pagination } from "../../components/Pagination";
import { SegmentedControl } from "../../components/SegmentedControl";
import { StatusPill } from "../../components/StatusPill";
import { Toolbar } from "../../components/Toolbar";
import { formatDateTime } from "../../formatting/date";
import { invalidatePrefix } from "../../state/queryClient";
import { buildAllowedSearchParams } from "../../state/routeState";
import { useQuery } from "../../state/useQuery";
import { listContacts } from "./api";
import { ContactImportPanel } from "./ContactImportPanel";
import {
  avatarColorsFor,
  formatCargoNivel,
  formatNivelLabel,
  initialsFor,
  parseShowNames,
  resolveContactStatus,
} from "./derived";
import {
  CONTACT_LIST_ALLOWED_KEYS,
  buildContactListSearchParams,
  normalizeContactListSearchParams,
} from "./filters";
import { ShowSelector } from "./ShowSelector";

/** REDISENO-PANEL.md §7.1: "buscador con ícono... aplicar con debounce 350 ms, sin botón 'Buscar'". */
const SEARCH_DEBOUNCE_MS = 350;

/** REDISENO-PANEL.md §7.1: "segmentado de estado: Todos / Activos / Suprimidos / En negociación". */
const ESTADO_TABS = [
  { value: "", label: "Todos" },
  { value: "Activo", label: "Activos" },
  { value: "Suprimido", label: "Suprimidos" },
  { value: "EnNegociacion", label: "En negociación" },
] as const;

/** Outline search glyph, same icon vocabulary (round caps/joins, no fill, `aria-hidden`) as every other v3 icon. */
function SearchIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={16}
      height={16}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      aria-hidden="true"
      className="v2-contacts-list__search-icon"
    >
      <path d="M11 18a7 7 0 1 0 0-14a7 7 0 0 0 0 14zM20 20l-4-4" />
    </svg>
  );
}

/**
 * One row-list `<li>`, shared by the desktop and mobile row lists below —
 * same fields, same markup, the ONLY difference is `showEmail` (design.md
 * §5.3: email is desktop-only; mobile reveals it on the detail route). Never
 * a second, independently maintained row template.
 */
function ContactRow({
  row,
  contextSuffix,
  showEmail,
}: {
  row: Contact;
  contextSuffix: string;
  showEmail: boolean;
}) {
  const { label, tone } = resolveContactStatus(row.estado);
  const shows = parseShowNames(row.shows);
  const colors = avatarColorsFor(row.contactId);
  return (
    <li className="v2-contacts-list__row-item">
      <Link to={`/contactos/${row.contactId}${contextSuffix}`} className="v2-contacts-list__row">
        <span
          className="v2-contacts-list__avatar"
          style={{ background: colors.bg, color: colors.fg }}
          aria-hidden="true"
        >
          {initialsFor(row.nombre)}
        </span>
        <span className="v2-contacts-list__row-identity">
          <span className="v2-contacts-list__row-name">{row.nombre}</span>
          {showEmail && <span className="v2-contacts-list__row-email">{row.email}</span>}
        </span>
        <span className="v2-contacts-list__row-company">
          <span>{row.empresa}</span>
          <span className="v2-contacts-list__row-meta">
            {formatCargoNivel(row.cargo, formatNivelLabel(row.nivelJerarquico))}
          </span>
        </span>
        <span className="v2-contacts-list__row-shows">
          {shows.length === 0 ? (
            <span className="v2-contacts-list__pill v2-contacts-list__pill--none">Sin shows</span>
          ) : (
            shows.map((name) => (
              // Codex PR #386 review (comment 4373699184): the pill now
              // ellipsizes a long Show name (contacts.css) -- `title` is the
              // fallback that still exposes the full value on hover/assistive
              // tech once it's truncated.
              <span key={name} className="v2-contacts-list__pill" title={name}>
                {name}
              </span>
            ))
          )}
        </span>
        <StatusPill label={label} tone={tone} />
      </Link>
    </li>
  );
}

/**
 * `/contactos` — REDISENO-PANEL.md §7.1. Header with the account-wide
 * "`total` contactos · `N` activos" subtitle (two independent bounded reads,
 * `size: 1`, never the current page's own filtered `data.total` — same
 * "independent small reads, no client-side total" convention
 * `OverviewScreen`'s own 3 independent `listCampaigns` reads already
 * established), a debounced search + estado `SegmentedControl` + Show
 * `<select>` filter bar, one row-list (avatar/empresa/shows/estado, no
 * `<table>`, no desktop/mobile duplication — same single-markup convention
 * `CampaignListScreen` already established), and a `Pagination
 * variant="range"` footer. URL still owns `estado`/`nivelJerarquico`/
 * `industria`/`showId`/`page`/`size` (`filters.ts` contract unchanged) even
 * though this screen's own filter bar only exposes estado/Show, per §7.1.
 */
export function ContactsScreen() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [q, setQ] = useState("");
  const [activeQ, setActiveQ] = useState("");

  const normalization = useMemo(() => normalizeContactListSearchParams(searchParams), [searchParams]);
  const { params, normalized, changed } = normalization;

  useEffect(() => {
    if (changed) {
      setSearchParams(normalized, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, changed]);

  // §7.1: no explicit "Buscar" button — debounce the typed value, same
  // 350ms-settle convention `CampaignCreatePanel`'s own `useDebouncedValue`
  // already established for its text criteria (there 400ms; here the spec's
  // own literal figure).
  useEffect(() => {
    const timeoutId = setTimeout(() => setActiveQ(q), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeoutId);
  }, [q]);

  const queryKey = useMemo(
    () => [
      "contacts",
      params.estado ?? null,
      params.nivelJerarquico ?? null,
      params.industria ?? null,
      params.showId ?? null,
      params.page,
      params.size,
      activeQ || null,
    ],
    [params.estado, params.nivelJerarquico, params.industria, params.showId, params.page, params.size, activeQ],
  );

  const { data, error, loading, stale, updatedAt, refetch } = useQuery({
    key: queryKey,
    queryFn: () =>
      listContacts({
        q: activeQ || undefined,
        estado: params.estado,
        nivelJerarquico: params.nivelJerarquico,
        industria: params.industria,
        showId: params.showId,
        page: params.page,
        size: params.size,
      }),
  });

  // §7.1 header subtitle: account-wide totals, independent of the current
  // filter/page/search -- two bounded (`size: 1`) reads, never a client-side
  // sum over the loaded page (§9.4 "nada más se calcula en el navegador").
  const { data: totalPage, stale: totalStale } = useQuery({
    key: ["contacts-count", "total"],
    queryFn: () => listContacts({ page: 1, size: 1 }),
  });
  const { data: activePage, stale: activeStale } = useQuery({
    key: ["contacts-count", "activo"],
    queryFn: () => listContacts({ page: 1, size: 1, estado: "Activo" }),
  });

  const contextSuffix = useMemo(() => {
    const allowed = buildAllowedSearchParams(searchParams.toString(), CONTACT_LIST_ALLOWED_KEYS);
    const asString = allowed.toString();
    return asString ? `?${asString}` : "";
  }, [searchParams]);

  const updateParams = useCallback(
    (next: {
      estado?: string;
      nivelJerarquico?: string;
      industria?: string;
      showId?: string;
      page: number;
      size: number;
    }) => {
      setSearchParams(buildContactListSearchParams(next));
    },
    [setSearchParams],
  );

  const handleEstadoChange = useCallback(
    (estado: string) =>
      updateParams({
        estado: estado || undefined,
        nivelJerarquico: params.nivelJerarquico,
        industria: params.industria,
        showId: params.showId,
        page: 1,
        size: params.size,
      }),
    [updateParams, params.nivelJerarquico, params.industria, params.showId, params.size],
  );

  const handleShowChange = useCallback(
    (showId: string) =>
      updateParams({
        estado: params.estado,
        nivelJerarquico: params.nivelJerarquico,
        industria: params.industria,
        showId: showId || undefined,
        page: 1,
        size: params.size,
      }),
    [updateParams, params.estado, params.nivelJerarquico, params.industria, params.size],
  );

  // Codex PR #386 review (comment 4373699158): nivelJerarquico/industria are
  // URL-owned filters this screen's own filter bar doesn't expose (§7.1), but
  // they ARE sent to listContacts above -- a bookmark/back-navigation URL
  // carrying only one of them and matching zero rows must show the FILTERED
  // empty state (clearFilters below actually removes them), never the
  // generic "no contacts yet" state a brand-new, unfiltered account sees.
  const hasActiveFilters = Boolean(
    params.estado || params.nivelJerarquico || params.industria || params.showId || activeQ,
  );

  // Codex review round 4 (P2, PR #386): the round-3 fix above only covered
  // the ZERO-result branch — a bookmarked/restored URL carrying
  // nivelJerarquico/industria that still MATCHES rows left the operator
  // looking at an unexplained subset with no way to discover or remove
  // either hidden filter at all (the toolbar only exposes estado/Show,
  // never these two — §7.1). Exposed as removable chips, the shared
  // `.v2-filter-chips` pattern (primitives.css, "Chips de filtro activo").
  const activeFilterChips = useMemo(
    () =>
      [
        params.nivelJerarquico
          ? { key: "nivelJerarquico" as const, label: `Nivel: ${formatNivelLabel(params.nivelJerarquico)}` }
          : null,
        params.industria ? { key: "industria" as const, label: `Industria: ${params.industria}` } : null,
      ].filter((chip): chip is { key: "nivelJerarquico" | "industria"; label: string } => chip !== null),
    [params.nivelJerarquico, params.industria],
  );

  const removeFilterChip = useCallback(
    (key: "nivelJerarquico" | "industria") => {
      updateParams({
        estado: params.estado,
        nivelJerarquico: key === "nivelJerarquico" ? undefined : params.nivelJerarquico,
        industria: key === "industria" ? undefined : params.industria,
        showId: params.showId,
        page: 1,
        size: params.size,
      });
    },
    [updateParams, params.estado, params.nivelJerarquico, params.industria, params.showId, params.size],
  );

  const clearFilters = useCallback(() => {
    setQ("");
    setActiveQ("");
    updateParams({
      estado: undefined,
      nivelJerarquico: undefined,
      industria: undefined,
      showId: undefined,
      page: 1,
      size: params.size,
    });
  }, [updateParams, params.size]);

  const handleImported = useCallback(() => {
    invalidatePrefix(["contacts"]);
    invalidatePrefix(["contacts-count"]);
    refetch();
  }, [refetch]);

  // Codex PR #386 review (id 4377311447): wiring "Ver contactos" to
  // handleShowChange preserved whatever search/estado/nivelJerarquico/
  // industria filters were already active when the import wizard was
  // opened -- the import always targets exactly ONE Show, so "Ver
  // contactos" must clear every other filter and show ONLY that Show.
  const handleViewImportedShow = useCallback(
    (importedShowId: string) => {
      setQ("");
      setActiveQ("");
      updateParams({
        estado: undefined,
        nivelJerarquico: undefined,
        industria: undefined,
        showId: importedShowId || undefined,
        page: 1,
        size: params.size,
      });
    },
    [updateParams, params.size],
  );

  return (
    <section aria-label="Contactos" className="v2-contacts-list">
      <header className="v2-contacts-list__header">
        <div className="v2-contacts-list__header-titles">
          <h1>Contactos</h1>
          {/* Codex round 6 (PR #386): a failed count refresh keeps the old
              `data` (stale) -- don't present it as the current totals. */}
          {totalPage && activePage && !totalStale && !activeStale && (
            <span className="v2-contacts-list__header-subtitle">
              {totalPage.total} {totalPage.total === 1 ? "contacto" : "contactos"} · {activePage.total} activos
            </span>
          )}
        </div>
        <ContactImportPanel onImported={handleImported} onViewContacts={handleViewImportedShow} />
      </header>

      <Toolbar ariaLabel="Filtros de contactos">
        <div className="v2-contacts-list__search">
          <Field label="Buscar por nombre, email o empresa" htmlFor="contacts-q">
            <span className="v2-contacts-list__search-field">
              <SearchIcon />
              <input
                id="contacts-q"
                value={q}
                onChange={(event) => setQ(event.target.value)}
                placeholder="Buscar por nombre, email o empresa"
              />
            </span>
          </Field>
        </div>

        <SegmentedControl
          ariaLabel="Filtrar contactos por estado"
          options={ESTADO_TABS.map((tab) => ({ value: tab.value, label: tab.label }))}
          value={params.estado ?? ""}
          onChange={handleEstadoChange}
        />

        <ShowSelector id="contacts-filter-showId" value={params.showId ?? ""} onChange={handleShowChange} allowEmpty hideLabel />
      </Toolbar>

      {activeFilterChips.length > 0 && (
        <ul className="v2-filter-chips">
          {activeFilterChips.map((chip) => (
            <li key={chip.key}>
              <button type="button" onClick={() => removeFilterChip(chip.key)}>
                {chip.label} · Quitar
              </button>
            </li>
          ))}
        </ul>
      )}

      {loading && <p role="status">Cargando contactos…</p>}

      {!data && error !== undefined && (
        <FeedbackPanel
          variant="error"
          message={describeError(error, "No se pudo cargar el listado de contactos.")}
          onRetry={refetch}
        />
      )}

      {data && (
        <>
          {stale && (
            <FeedbackPanel
              variant="stale"
              message={`Datos anteriores${updatedAt ? ` · ${formatDateTime(new Date(updatedAt).toISOString())}` : ""}`}
              onRetry={refetch}
            />
          )}

          {data.items.length === 0 ? (
            // Codex PR #386 review (id 4377311455): a valid bookmark/restored
            // URL whose `page` is now beyond the last page gets `items: []`
            // from the API while `total` stays positive -- that's a stale
            // page number, never "no contacts match" nor "brand-new empty
            // account" (pagination isn't considered an active filter, so
            // `hasActiveFilters` can't tell these apart on its own). Checked
            // first since it applies whether or not a filter is active.
            data.total > 0 ? (
              <EmptyState
                title="Esta página no tiene contactos"
                description="La página que buscas ya no existe para este filtro."
                actionLabel="Ir a la última página"
                onAction={() => {
                  // `data.size` is the API RESPONSE's own size, not the
                  // already-validated `params.size` (filters.ts clamps that
                  // to 1..50) -- a non-positive or non-finite response size
                  // would make `Math.ceil(total / size)` yield `Infinity`/
                  // `NaN`, which URL normalization then silently resets to
                  // page 1 instead of the real last page. Fall back to the
                  // requested page size, or 1 as a last resort.
                  const effectiveSize =
                    Number.isFinite(data.size) && data.size > 0 ? data.size : params.size > 0 ? params.size : 1;
                  updateParams({
                    estado: params.estado,
                    nivelJerarquico: params.nivelJerarquico,
                    industria: params.industria,
                    showId: params.showId,
                    page: Math.max(1, Math.ceil(data.total / effectiveSize)),
                    size: effectiveSize,
                  });
                }}
              />
            ) : hasActiveFilters ? (
              <EmptyState
                title="Ningún contacto coincide"
                description="Cambia la búsqueda o los filtros."
                actionLabel="Limpiar filtros"
                onAction={clearFilters}
              />
            ) : (
              <EmptyState
                title="Aún no tienes contactos"
                description="Importa un archivo CSV o XLSX para empezar."
              />
            )
          ) : (
            <>
              {/* design.md §5.3: "Desktop shows email/name/company/status; mobile
                  shows name/company/status first and reveals email in the record
                  detail, reducing shoulder-surfing without removing authorized
                  access." Both lists render from the SAME `data.items` (never two
                  independently fetched/paginated copies); only `showEmail` differs
                  between them, same mobile/desktop CSS-toggle convention
                  `SegmentsScreen`'s own `DataTable`/`RecordList` pair already
                  established (segments.css's `.v2-u-table-scroll`/`.v2-record-list`
                  display toggle at 768px). */}
              <ul aria-label="Contactos" className="v2-contacts-list__rows v2-contacts-list__rows--desktop">
                {data.items.map((row) => (
                  <ContactRow key={row.contactId} row={row} contextSuffix={contextSuffix} showEmail />
                ))}
              </ul>
              <ul
                aria-label="Contactos (vista móvil)"
                className="v2-contacts-list__rows v2-contacts-list__rows--mobile"
              >
                {data.items.map((row) => (
                  <ContactRow key={row.contactId} row={row} contextSuffix={contextSuffix} showEmail={false} />
                ))}
              </ul>
              <Pagination
                variant="range"
                page={data.page}
                size={data.size}
                total={data.total}
                onPageChange={(nextPage) =>
                  updateParams({
                    estado: params.estado,
                    nivelJerarquico: params.nivelJerarquico,
                    industria: params.industria,
                    showId: params.showId,
                    page: nextPage,
                    size: data.size,
                  })
                }
                onSizeChange={(nextSize) =>
                  updateParams({
                    estado: params.estado,
                    nivelJerarquico: params.nivelJerarquico,
                    industria: params.industria,
                    showId: params.showId,
                    page: 1,
                    size: nextSize,
                  })
                }
              />
            </>
          )}
        </>
      )}
    </section>
  );
}
